import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Consent Center — per-user, versioned, audit-logged consent state.
 *
 * Built alongside `/app/memory/legal/14_FOUNDER_MONETIZATION_PARTNERSHIP_BRIEF.md`
 * and re-scoped per `15_AGGREGATE_FLOOR_DATA_DISCIPLINE.md`.
 *
 * Each toggle here is the user-facing surface for one consent class in the
 * brief. Anti-shame copy: explain what each toggle does, what it costs the
 * user to turn off (nothing — they don't lose features), and what the default
 * is. Toggles are independent and revocable at any time.
 *
 * Scope discipline: this UI exposes ONLY consents that govern identifiable
 * user data or downstream features the user can actually toggle. The earlier
 * `ai_training_on_my_data` toggle was removed — Enferno's training
 * of its own internal models happens on statistically de-identified data,
 * which is not personal information under CPRA § 1798.140 / GDPR Recital 26.
 * That activity is now disclosed in the Privacy Policy and Public Data Use
 * page, governed by the k-anonymity floor in Brief 15, not by a user toggle.
 * (Identifiable-data training would require a separate explicit consent flow;
 * no such activity is contemplated.)
 *
 * Wiring: today this just stores the preference. As peer-matching and any
 * future opt-in connection features ship, each will read this consent state
 * at runtime before acting on the user's data.
 */

const TOGGLES = [
  {
    key: 'ai_data_personalization',
    title: 'Personalize my AI coach with my profile',
    description:
      "On: your Coach tailors workouts and answers using your setup profile — experience, commitment, program type, equipment, goals, and your free-text limitations and injuries (all editable above). To do that, those details are sent to Anthropic (Claude) to build your workout or answer your question. Off: Coach replies to each message on its own, without your history, limitations, or goals — you keep full access either way.",
    defaultOn: true,
    invert: false,
  },
  {
    key: 'peer_matching_visible',
    title: 'Let other Enferno users find me for peer matching',
    description:
      "When we ship features that connect users with peers (similar conditions, training goals, mentor pairing), turning this on makes you discoverable. Off by default. You'll still be able to use every connection feature; you just won't appear in the directory.",
    defaultOn: false,
    invert: false,
  },
  {
    key: 'marketing_communications',
    title: 'Send me product updates and founder notes',
    description:
      "Occasional emails about new features, mission updates, and the IronLimb Foundation's work. We never spam. Off means transactional email only (auth, billing, security). Transactional email never depends on this toggle.",
    defaultOn: false,
    invert: false,
  },
  {
    key: 'do_not_sell_or_share',
    title: 'Do Not Sell or Share My Personal Information',
    description:
      "California / CPRA opt-out. Enferno does not sell or share data that identifies you with marketing partners, ad networks, or third parties. Turning this on locks that promise in for your account: any future flow involving information that identifies you will exclude you. Aggregate, statistically de-identified business metrics (total users, retention rates, average activity — nothing that points at you) are not personal information under CPRA and are not affected by this toggle.",
    defaultOn: false,
    invert: false,
  },
];

const ConsentCenter = () => {
  const [consents, setConsents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/account/consents`);
        if (!cancelled) setConsents(r.data.consents || {});
      } catch (err) {
        console.warn('Failed to load consents:', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (key, nextValue) => {
    if (busy[key]) return;
    setBusy((b) => ({ ...b, [key]: true }));
    // Optimistic
    setConsents((c) => ({ ...c, [key]: nextValue }));
    try {
      const r = await axios.patch(`${API}/account/consents`, { [key]: nextValue });
      setConsents(r.data.consents);
    } catch (err) {
      // Rollback
      setConsents((c) => ({ ...c, [key]: !nextValue }));
      toast.error('Could not save that change. Try again in a moment.');
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="mt-5 p-5 border border-enf-surface-border rounded-xl bg-enf-surface-deep flex items-center gap-3" data-testid="consent-center-loading">
        <Loader2 className="w-4 h-4 animate-spin text-enf-text-muted" />
        <span className="text-xs text-enf-text-muted">Loading your data preferences…</span>
      </div>
    );
  }

  return (
    <div className="mt-5 p-5 border border-enf-surface-border rounded-xl bg-enf-surface-deep" data-testid="consent-center">
      <div className="flex items-start gap-3 mb-4">
        <Shield className="w-4 h-4 text-enf-role-info mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-semibold">Your data, your choices</div>
          <div className="text-xs text-enf-text-muted mt-1 max-w-2xl">
            Each toggle is independent and revocable any time. None of these change what features you have access to — they only control how your data is used behind the scenes. Full details at{' '}
            <a href="https://ironfiretech.com/enferno/data-use" target="_blank" rel="noopener noreferrer" className="text-enf-role-info hover:underline">ironfiretech.com/enferno/data-use</a>.
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {TOGGLES.map(({ key, title, description, defaultOn }) => {
          const value = consents?.[key] ?? (defaultOn ? true : false);
          const isBusy = busy[key];
          return (
            <div key={key} className="flex items-start justify-between gap-4 p-3 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid={`consent-toggle-${key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-enf-text-primary">{title}</div>
                <div className="text-xs text-enf-text-muted mt-1">{description}</div>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(key, !value)}
                disabled={isBusy}
                aria-pressed={value}
                className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-colors flex-shrink-0 mt-0.5 ${
                  value
                    ? 'bg-enf-role-info border-enf-role-info'
                    : 'bg-enf-surface-deep border-enf-surface-border'
                } ${isBusy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid={`consent-toggle-${key}-button`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
                {isBusy && (
                  <Loader2 className="absolute inset-0 m-auto w-3 h-3 text-white animate-spin" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConsentCenter;
